
import { useTranslation } from "react-i18next";
import { useTheme, useTenantApp } from "../../hooks";
import { useMemo, useState, lazy, Suspense, type ReactNode } from "react";
import { Icon } from "../ui/Icon";

const VersionDialog = lazy(() => import("./VersionDialog"));
const AboutDialog = lazy(() => import("./AboutDialog"));

interface ActionItem { label: string; icon: ReactNode; }

function Navbar() {

    const { app } = useTenantApp();
    const { theme, darkTheme, toggleIsDisabled, toggleTheme } = useTheme()

    const { t, i18n } = useTranslation()

    const [showVersionDialog, setShowVersionDialog] = useState(false);
    const [showAboutDialog, setShowAboutDialog] = useState(false);

    const languageOptions = app?.languages || [
        { code: 'en', label: 'English' },
        { code: 'it', label: 'Italiano' }
    ];

    const actions: ActionItem[] = useMemo(() => [
        { label: 'version', icon: <Icon name="info" className="text-2xl leading-none" /> },
        { label: 'about', icon: <Icon name="star" className="text-2xl leading-none" /> }],
        []);

    const doAction = (action: string) => {
        switch (action) {
            case 'version':
                setShowVersionDialog(true);
                break;
            case 'about':
                setShowAboutDialog(true);
                break;
        }
    }

    const useWhiteFilter = useMemo(() => !toggleIsDisabled && theme === darkTheme && app?.branding.logoDark, [toggleIsDisabled, theme, darkTheme, app]);

    return (
        <>
            <div className="navbar bg-base-100 shadow-sm">
                <div className="flex flex-1 flex-row justify-between">
                    <div className="flex flex-row">
                        <img
                            src={`${app?.branding?.logo || '/logo.png'}`}
                            alt="ParkMap Logo"
                            className={`me-2 ${useWhiteFilter && 'white-filter'}`}
                            style={{ width: `${app!.branding!.logoSize / 4}rem` }} />
                        <div className="flex flex-col gap-0 justify-center">
                            {app?.branding.showNameInHeader && <div className="font-mono">{app.name}</div>}
                            {app?.branding.showDescriptionInHeader && <div className="badge badge-xs rounded-none badge-primary">{app.description}</div>}
                        </div>
                    </div>
                    {/* Right side controls */}
                    <div className="flex items-center gap-1">
                        {/* Theme toggle */}
                        {!toggleIsDisabled && (
                            <label className="swap swap-rotate btn btn-ghost btn-sm px-1.5">
                                <input
                                    type="checkbox"
                                    checked={theme === darkTheme}
                                    onChange={() => toggleTheme()}
                                />
                                {/* Quando checked (dark mode), mostra il sole */}
                                <Icon name="light_mode" className="text-2xl leading-none swap-on" />

                                {/* Quando unchecked (light mode), mostra la luna */}
                                <Icon name="dark_mode" className="text-2xl leading-none swap-off" />
                            </label>
                        )}
                        {/* Language selector */}
                        {languageOptions.length > 1 && (
                            <div className="dropdown dropdown-bottom dropdown-end">
                                <button tabIndex={0} className="btn btn-ghost btn-sm px-1.5">
                                    <Icon name="translate" className="text-2xl leading-none" />
                                    <Icon name="keyboard_arrow_down" className="text-2xl leading-none" />
                                </button>
                                <ul tabIndex={-1} className="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 mt-5 shadow-sm">
                                    {languageOptions.map(({ code, label }) => (
                                        <li key={code} onClick={() => {
                                            i18n.changeLanguage(code);
                                            localStorage.setItem('locale', code);
                                        }}>
                                            <button className={i18n.language === code ? 'menu-active' : ''}>
                                                <span className="pe-4 font-mono text-[.5625rem] font-bold tracking-[0.09375rem] opacity-40 uppercase">{code}</span>
                                                <span className="font-[sans-serif]">{label}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="dropdown dropdown-bottom dropdown-end">
                            <button tabIndex={0} className="btn btn-dash btn-primary btn-sm px-1.5">
                                <Icon name="help" className="text-2xl leading-none" />
                                <Icon name="keyboard_arrow_down" className="text-2xl leading-none" />
                            </button>
                            <ul tabIndex={-1} className="dropdown-content menu bg-base-100 rounded-box z-1 w-48 mt-5 shadow-sm">
                                {actions.map(({ label }, k) => (
                                    <li key={k} onClick={() => {
                                        doAction(label);
                                    }}>
                                        <button className="flex justify-between items-center w-full">
                                            <span className="font-[sans-serif] capitalize">{t(`navbar.${label}.label`)}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <Suspense fallback={null}>
                <VersionDialog
                    show={showVersionDialog}
                    onClose={() => setShowVersionDialog(false)}
                />

                <AboutDialog
                    show={showAboutDialog}
                    onClose={() => setShowAboutDialog(false)}
                />
            </Suspense>
        </>
    )
}

export default Navbar;